// angular import
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Project import
import { LoginGuard } from './guards/auth-login.guard';
import { AuthGuard } from './guards/auth.guard';
import { AdminComponent } from './theme/layouts/admin-layout/admin-layout.component';
import { GuestLayoutComponent } from './theme/layouts/guest-layout/guest-layout.component';

const routes: Routes = [
	{
		path: '',
		redirectTo: 'login',
		pathMatch: 'full'
	},
	{
		path: 'service/rating/:id',
		loadComponent: () =>
			import('./services/ui/service-rating/service-rating.component').then((c) => c.ServiceRatingComponent)
	},
	{
		path: 'terms-conditions',
		loadComponent: () => import('./terms-conditions/terms-conditions.component').then((c) => c.TermsConditionsComponent)
	},
	{
		path: 'terms-conditions-driver',
		loadComponent: () =>
			import('./terms-conditions-driver/terms-conditions-driver.component').then(
				(c) => c.TermsConditionsDriverComponent
			)
	},
	{
		path: 'data-deletion-policy',
		loadComponent: () =>
			import('./data-deletion-policy/data-deletion-policy.component').then((c) => c.DataDeletionPolicyComponent)
	},
	{
		path: 'privacy-policy',
		loadComponent: () => import('./privacy-policy/privacy-policy.component').then((c) => c.PrivacyPolicyComponent)
	},
	{
		path: '',
		component: AdminComponent,
		children: [
			{
				path: 'dashboard/home',
				canMatch: [AuthGuard],
				loadComponent: () => import('./dashboard/feature/home/home.component').then((c) => c.HomeComponent)
			},
			{
				path: 'driver/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/driver-list/driver-list.component').then((c) => c.DriverListComponent)
			},
			{
				path: 'driver/create',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/driver-create/driver-create.component').then((c) => c.DriverCreateComponent)
			},
			{
				path: 'driver/create/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/driver-create/driver-create.component').then((c) => c.DriverCreateComponent)
			},
			{
				path: 'driver/settle-trips/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/settlement/settlement.component').then((c) => c.SettlementComponent)
			},
			{
				path: 'driver/settlement',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/settlement/settlement.component').then((c) => c.SettlementComponent)
			},
			{
				path: 'services/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/services-list/services-list.component').then((c) => c.ServicesListComponent)
			},
			{
				path: 'services/list/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/ui/service-detail/service-detail.component').then((c) => c.ServiceDetailComponent)
			},
			{
				path: 'passenger/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./passenger/feature/passenger-list/passenger-list.component').then((c) => c.PassengerListComponent)
			},
			{
				path: 'brands/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./brands/feature/brands-list/brands-list.component').then((c) => c.BrandsListComponent)
			},
			{
				path: 'category/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./category/feature/category-list/category-list.component').then((c) => c.CategoryListComponent)
			},
			{
				path: 'vehicle/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/feature/vehicle-list/vehicle-list.component').then((c) => c.VehicleListComponent)
			},
			{
				path: 'admin/list',
				canMatch: [AuthGuard],
				loadComponent: () => import('./admin/feature/admin-list/admin-list.component').then((c) => c.AdminListComponent)
			},
			{
				path: 'coupon/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./coupon/feature/coupon-list/coupon-list.component').then((c) => c.CouponListComponent)
			},
			{
				path: 'parameters/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./parameters/feature/parameters-list/parameters-list.component').then(
						(c) => c.ParametersListComponent
					)
			},
			{
				path: 'create/vehicle',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/feature/create-vehicle/create-vehicle.component').then((c) => c.CreateVehicleComponent)
			},
			{
				path: 'create/vehicle/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/feature/create-vehicle/create-vehicle.component').then((c) => c.CreateVehicleComponent)
			},
			{
				path: 'detail/vehicle/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/ui/detail-vehicle/detail-vehicle.component').then((c) => c.DetailVehicleComponent)
			},
			{
				path: 'detail/driver/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/detail/driver-detail/driver-detail.component').then((c) => c.DriverDetailComponent)
			},
			{
				path: 'services/history',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/history-services-list/history-services-list.component').then(
						(c) => c.HistoryServicesListComponent
					)
			},
			{
				path: 'services/history/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/history-service-detail/history-service-detail.component').then(
						(c) => c.HistoryServiceDetailComponent
					)
			},
			{
				path: 'rates/local',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/feature/rates-local-list/rates-local-list.component').then((c) => c.RatesLocalListComponent)
			},
			{
				path: 'rates/foreigner',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/feature/rates-foreigner-list/rates-foreigner-list.component').then(
						(c) => c.RatesForeignerListComponent
					)
			},
			{
				path: 'pre-operational/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./pre-operational/feature/inspections-list/inspections-list.component').then(
						(c) => c.InspectionsListComponent
					)
			},
			{
				path: 'pre-operational/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./pre-operational/feature/inspections-detail/inspections-detail.component').then(
						(c) => c.InspectionsDetailComponent
					)
			},
			{
				path: 'fuec-config',
				canMatch: [AuthGuard],
				loadChildren: () => import('./fuec-config/feature/fuec-config.module').then((m) => m.FuecConfigModule)
			},
			{
				path: 'helplines/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./helplines/feature/helplines-list/helplines-list.component').then((c) => c.HelplinesListComponent)
			},
			{
				path: 'events/catalog',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./events-catalog/feature/events-catalog-list/events-catalog-list.component').then(
						(c) => c.EventsCatalogListComponent
					)
			},
			{
				path: 'push-notifications/send',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./push-notifications/feature/push-notifications-send/push-notifications-send.component').then(
						(c) => c.PushNotificationsSendComponent
					)
			},
			{
				path: 'enterprise/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-list/enterprise-list.component').then(
						(c) => c.EnterpriseListComponent
					)
			},
			{
				path: 'enterprise/create',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-create/enterprise-create.component').then(
						(c) => c.EnterpriseCreateComponent
					)
			},
			{
				path: 'enterprise/create/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-create/enterprise-create.component').then(
						(c) => c.EnterpriseCreateComponent
					)
			},
			{
				path: 'enterprise/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-detail/enterprise-detail.component').then(
						(c) => c.EnterpriseDetailComponent
					)
			},
			{
				path: 'enterprise/beneficiaries/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/beneficiaries-list/beneficiaries-list.component').then(
						(c) => c.BeneficiariesListComponent
					)
			},
			{
				path: 'profile',
				canMatch: [AuthGuard],
				loadComponent: () => import('./profile/feature/profile/profile.component').then((c) => c.ProfileComponent)
			},
			{
				path: 'notifications/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./notifications/feature/notifications-list/notifications-list.component').then(
						(c) => c.NotificationsListComponent
					)
			},
			{
				path: 'app-versions/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./app-versions/feature/app-versions-list/app-versions-list.component').then(
						(c) => c.AppVersionsListComponent
					)
			},
			{
				path: 'fields-forms/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./fields-forms/feature/fields-forms-list/fields-forms-list.component').then(
						(c) => c.FieldsFormsListComponent
					)
			},
			{
				path: 'drivers/map',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./dashboard/feature/drivers-map/drivers-map.component').then((c) => c.DriversMapComponent)
			},
			{
				path: 'geofences/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./geofences/feature/geofences-list/geofences-list.component').then((c) => c.GeofencesListComponent)
			},
			{
				path: 'geofences/create',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./geofences/feature/geofence-form/geofence-form.component').then((c) => c.GeofenceFormComponent)
			},
			{
				path: 'geofences/edit/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./geofences/feature/geofence-form/geofence-form.component').then((c) => c.GeofenceFormComponent)
			},
			{
				path: 'geofences/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./geofences/feature/geofence-detail/geofence-detail.component').then((c) => c.GeofenceDetailComponent)
			},
			{
				path: 'planned-routes/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./planned-routes/feature/planned-routes-list/planned-routes-list.component').then(
						(c) => c.PlannedRoutesListComponent
					)
			},
			{
				path: 'planned-routes/create',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./planned-routes/feature/planned-route-form/planned-route-form.component').then(
						(c) => c.PlannedRouteFormComponent
					)
			},
			{
				path: 'planned-routes/edit/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./planned-routes/feature/planned-route-form/planned-route-form.component').then(
						(c) => c.PlannedRouteFormComponent
					)
			},
			{
				path: 'planned-routes/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./planned-routes/feature/planned-route-detail/planned-route-detail.component').then(
						(c) => c.PlannedRouteDetailComponent
					)
			},
			{
				path: 'routes/history',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./routes/feature/route-history/route-history.component').then((c) => c.RouteHistoryComponent)
			},
			{
				path: 'events/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./events/feature/events-list/events-list.component').then((c) => c.EventsListComponent)
			},
			{
				path: 'events/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./events/feature/events-detail/events-detail.component').then((c) => c.EventsDetailComponent)
			}
		]
	},
	{
		path: '',
		component: GuestLayoutComponent,
		children: [
			{
				path: 'login',
				canMatch: [LoginGuard],
				loadComponent: () =>
					import('./login/feature/login-admin/login-admin.component').then((c) => c.LoginAdminComponent)
			},
			{
				path: 'login/password',
				loadComponent: () =>
					import('./login/feature/recover-password/recover-password.component').then((c) => c.RecoverPasswordComponent)
			},
			{
				path: 'otp/validation',
				loadComponent: () =>
					import('./login/feature/otp-validation/otp-validation.component').then((c) => c.OtpValidationComponent)
			},
			{
				path: 'change/password',
				loadComponent: () =>
					import('./login/feature/change-password/change-password.component').then((c) => c.ChangePasswordComponent)
			}
		]
	},
	{
		path: '**',
		redirectTo: 'login-admin',
		pathMatch: 'full'
	}
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
export class AppRoutingModule {}
